import * as Crypto from 'expo-crypto';
import { apiGet, apiPost, apiDelete, apiProjectsPost } from '../../utilities/apiClient';
import { getSessionIds } from './_sessionIds';
import config from '../../config';
import {
  SET_SNAPSHOT_VERSIONS,
  SET_SNAPSHOT_SYNCING,
  SET_SNAPSHOT_LAST_BACKUP,
  SET_SNAPSHOT_ERROR,
  SET_SNAPSHOT_BACKUP_STATUS,
  SET_SNAPSHOT_VIEW_MODE,
  CLEAR_SNAPSHOT_VIEW_MODE,
  SET_SNAPSHOT_SHARES,
  REMOVE_SNAPSHOT_SHARE,
} from '../constants';
import {
  cacheSnapshotKey,
  loadSnapshotKey,
  decryptSnapshot,
  encryptSnapshotForShare,
  generateSnapshotShareKey,
  bytesToHex,
  deriveSmkForEnvelope,
  deriveSmkForNewSnapshot,
  getEnvelopeVersion,
  SNAPSHOT_ENVELOPE_VERSION_V2,
} from '../../utilities/snapshotCrypto';
import {
  generateAccountRootKey,
  installAccountRootKey,
  hasAccountRootKey,
  wipeAccountRootKey,
} from '../../utilities/accountRootKey';
import {
  generateRecoveryMnemonic,
  isValidRecoveryMnemonic,
  wrapAccountRootKey,
  unwrapAccountRootKey,
  cacheMnemonicLocally,
  getCachedMnemonic,
  clearCachedMnemonic,
  RECOVERY_WRAP_VERSION,
} from '../../utilities/recoveryMnemonic';
import { deserializeSnapshot } from '../../utilities/snapshotSerializer';
import {
  saveSnapshot,
  loadSnapshot,
  checkBackupStatus,
  syncSnapshotToICloud,
  envelopeChecksum,
  hydrateLatestFromICloud,
} from '../../utilities/snapshotStorage';

// ─── Fetch version history ────────────────────────────────────────────────────

export const fetchSnapshotVersions = (projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return;
  try {
    const res = await apiGet(`/snapshots/versions/${projectId}/${accountId}/${cognitoId}`);
    const versions = Array.isArray(res?.payload) ? res.payload : [];
    dispatch({ type: SET_SNAPSHOT_VERSIONS, payload: { project_id: projectId, versions } });
  } catch {
    dispatch({ type: SET_SNAPSHOT_VERSIONS, payload: { project_id: projectId, versions: [] } });
  }
};

// ─── v2 recovery: setup, restore, lazy code reveal ───────────────────────────
// The canonical 32-byte account root key (see accountRootKey.ts) is the
// secret that v2 snapshots derive from. The server only ever stores it
// wrapped (mnemonic-derived). These actions are how the root key gets onto
// every device the user signs into:
//
//   - getSnapshotRecoveryStatus: quick local + server probe — used to gate
//                                "do we need to setup or restore?" UI.
//   - setupSnapshotRecovery:     first device → generate, wrap, upload, install.
//   - restoreSnapshotRecovery:   any new device → fetch wrap, unwrap, install.
//   - revealRecoveryMnemonic:    show me my code (lazy, from local cache).
//   - resetSnapshotRecoveryLocal: nuke local key + cached mnemonic on this
//                                 device (server wrap untouched — recovery
//                                 still works).
//
// The "lazy" UX choice means we DO NOT show the mnemonic during setup.
// Setup can run silently the first time the user backs up, and the
// mnemonic is available later via `revealRecoveryMnemonic` from a
// settings screen. Settings UI that explicitly walks the user through
// setup is free to surface `setup()`'s returned mnemonic immediately.

export interface RecoveryStatus {
  hasLocalKey: boolean;     // root key installed on this device
  serverHasWrap: boolean;   // server has a wrap (recovery is set up somewhere)
  needsRecovery: boolean;   // user must complete the recovery flow before snapshotting
}

/**
 * Look at the server + this device and report what state recovery is in.
 * Callers use this on app launch / before opening the snapshot UI to decide
 * whether to surface the "set up backup" sheet or the "enter recovery code"
 * sheet (or neither, when everything is already in place).
 */
export const getSnapshotRecoveryStatus = () => async (): Promise<RecoveryStatus> => {
  const { accountId, cognitoId } = getSessionIds();
  const empty: RecoveryStatus = {
    hasLocalKey: false, serverHasWrap: false, needsRecovery: false,
  };
  if (!accountId || !cognitoId) return empty;

  const [hasLocalKey, checkRes] = await Promise.all([
    hasAccountRootKey(accountId),
    apiGet(`/snapshots/recovery/check/${accountId}/${cognitoId}`).catch(() => null),
  ]);
  const serverHasWrap = Boolean(checkRes?.payload?.exists);
  return {
    hasLocalKey,
    serverHasWrap,
    needsRecovery: serverHasWrap && !hasLocalKey,
  };
};

/**
 * First-device setup. Generates the canonical root key, generates a 12-word
 * BIP39 mnemonic, HKDF-SHA256-wraps the root key, uploads the wrap to the
 * server, and installs the root key locally.
 *
 * Returns the mnemonic exactly once on success — caller decides whether to
 * surface it now (forced display) or stash it for lazy reveal later. The
 * mnemonic is also cached locally so the lazy "show me my code" path works
 * without re-derivation.
 *
 * If recovery already exists on the server (other device set it up first),
 * returns { needsRecovery: true } and does nothing — caller should route to
 * `restoreSnapshotRecovery`.
 */
export const setupSnapshotRecovery = (
  opts?: { force?: boolean },
) => async (dispatch: any): Promise<{
  success: boolean; mnemonic?: string; needsRecovery?: boolean; message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  try {
    if (!opts?.force) {
      const checkRes = await apiGet(`/snapshots/recovery/check/${accountId}/${cognitoId}`);
      if (checkRes?.payload?.exists) {
        return {
          success: false,
          needsRecovery: true,
          message: 'Recovery is already set up on another device. Restore using your recovery code.',
        };
      }
    }

    const rootKey = await generateAccountRootKey();
    const mnemonic = generateRecoveryMnemonic();

    // We need the per-account wrap salt up-front because the wrap KEY is
    // derived from (mnemonic, salt) — the server can't substitute a salt
    // for us after the fact without orphaning the wrap. Generate it
    // client-side; the server's /store endpoint requires it on init and
    // will reject any later attempt to change it (the DB trigger blocks
    // an UPDATE of `wrap_salt_hex` outright).
    const wrapSaltHex = bytesToHex(new Uint8Array(await Crypto.getRandomBytesAsync(32)));
    const wrap = await wrapAccountRootKey(rootKey, mnemonic, wrapSaltHex);

    const storeRes = await apiPost(`/snapshots/recovery/store/${accountId}/${cognitoId}`, {
      mode: 'init',
      wrap_version: wrap.wrap_version,
      kdf: wrap.kdf,
      kdf_params: wrap.kdf_params,
      wrap_salt_hex: wrap.wrap_salt_hex,
      wrap_iv_hex: wrap.wrap_iv_hex,
      wrap_tag_hex: wrap.wrap_tag_hex,
      wrapped_root_key_hex: wrap.wrapped_root_key_hex,
    });

    if (!storeRes?.success) {
      // Most likely cause: another device completed init between our
      // pre-check and our POST (the server returns 409). Re-read status so
      // we can correctly route the caller to the restore flow rather than
      // retrying init in a loop.
      const post = await dispatch(getSnapshotRecoveryStatus());
      return {
        success: false,
        needsRecovery: post.needsRecovery || post.serverHasWrap,
        message: storeRes?.message ?? 'Failed to store recovery wrap.',
      };
    }

    await installAccountRootKey(accountId, rootKey);
    // Server echoes the rev it just persisted (1 on fresh init). Fall
    // back to 1 defensively if the server is older and didn't echo —
    // that matches the default value of the new column.
    const serverRev = Number(storeRes?.payload?.wrap_rev);
    const wrapRev = Number.isFinite(serverRev) && serverRev >= 1 ? Math.trunc(serverRev) : 1;
    await cacheMnemonicLocally(accountId, mnemonic, wrapRev);

    return { success: true, mnemonic };
  } catch (err: any) {
    return { success: false, message: err?.message ?? 'Recovery setup failed.' };
  }
};

/**
 * Second-device / new-install restore. Fetches the wrap, derives the wrap
 * key from the user-supplied mnemonic, unwraps the root key, installs it
 * locally, and caches the mnemonic for later lazy reveal.
 *
 * Validates the mnemonic against BIP39 first so an obvious typo fails fast
 * before any network round-trip or KDF derivation.
 */
export const restoreSnapshotRecovery = (
  mnemonic: string,
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  if (!isValidRecoveryMnemonic(mnemonic)) {
    return { success: false, message: 'That recovery code does not look like a valid 12-word BIP39 phrase.' };
  }

  try {
    const wrappedRes = await apiGet(`/snapshots/recovery/wrapped/${accountId}/${cognitoId}?form=mnemonic`);
    if (!wrappedRes?.success || !wrappedRes?.payload) {
      return { success: false, message: wrappedRes?.message ?? 'Recovery is not set up for this account.' };
    }
    const wrap = {
      wrap_version: wrappedRes.payload.wrap_version,
      kdf: wrappedRes.payload.kdf,
      kdf_params: wrappedRes.payload.kdf_params,
      wrap_salt_hex: wrappedRes.payload.wrap_salt_hex,
      wrap_iv_hex: wrappedRes.payload.wrap_iv_hex,
      wrap_tag_hex: wrappedRes.payload.wrap_tag_hex,
      wrapped_root_key_hex: wrappedRes.payload.wrapped_root_key_hex,
    };
    if (wrap.wrap_version !== RECOVERY_WRAP_VERSION) {
      return { success: false, message: `Unsupported recovery wrap version (${wrap.wrap_version}). Update the app.` };
    }
    const rootKey = await unwrapAccountRootKey(wrap as any, mnemonic);
    if (!rootKey) {
      return { success: false, message: 'Recovery code did not unlock your snapshots. Check the words and try again.' };
    }
    await installAccountRootKey(accountId, rootKey);
    // The /wrapped response includes the wrap_rev that corresponds to
    // the wrap we just successfully unwrapped; cache the mnemonic at
    // that rev so future reveal calls on this device know when they've
    // gone stale (because a different device rotated after us).
    const serverRev = Number(wrappedRes.payload.wrap_rev);
    const wrapRev = Number.isFinite(serverRev) && serverRev >= 1 ? Math.trunc(serverRev) : 1;
    await cacheMnemonicLocally(accountId, mnemonic, wrapRev);
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message ?? 'Recovery restore failed.' };
  }
};

export interface RevealMnemonicResult {
  /** The mnemonic to display, or null if nothing can be safely surfaced. */
  mnemonic: string | null;
  /**
   * True only when we *actively detected* the cached mnemonic is stale
   * (server wrap_rev is ahead of our cached rev). This is the case the UI
   * should message specifically: "your code was changed on another
   * device; type in your current code to re-cache it here."
   */
  stale?: boolean;
  /**
   * True when we couldn't reach the server to check freshness. We still
   * return the cached mnemonic but the UI should warn the user we
   * couldn't verify it's the current one.
   */
  unverified?: boolean;
  message?: string;
}

/**
 * "Show me my recovery code." Returns the cached mnemonic on this device,
 * but first checks that it still matches the current server wrap — if a
 * different device has since rotated the mnemonic, the cached words are
 * stale (they no longer unlock the live wrap) and displaying them would
 * trick the user into writing down something that can't recover the
 * account. In that case we wipe the cache and tell the caller.
 *
 * Returns null mnemonic when:
 *   - user not authenticated
 *   - nothing is cached on this device
 *   - the cache was detected stale (and is now cleared)
 *
 * The freshness check is a single /recovery/check round-trip; /check
 * returns `wrap_rev`, a monotonic counter the server bumps on every
 * rotate-mnemonic call. If the server is offline we mark the result as
 * `unverified` and return the cached mnemonic anyway — refusing to show
 * the code whenever the network is down would be a worse UX than a
 * banner saying we couldn't double-check.
 */
export const revealRecoveryMnemonic = () => async (): Promise<RevealMnemonicResult> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId) return { mnemonic: null };

  const cached = await getCachedMnemonic(accountId);
  if (!cached) return { mnemonic: null };

  if (!cognitoId) {
    // Signed-in-but-no-cognitoId shouldn't happen in practice; returning
    // the cached value here matches the offline branch below.
    return { mnemonic: cached.mnemonic, unverified: true };
  }

  let serverRev: number | null = null;
  try {
    const checkRes = await apiGet(`/snapshots/recovery/check/${accountId}/${cognitoId}`);
    const raw = Number(checkRes?.payload?.wrap_rev);
    serverRev = Number.isFinite(raw) && raw >= 1 ? Math.trunc(raw) : null;
  } catch {
    return { mnemonic: cached.mnemonic, unverified: true };
  }

  if (serverRev === null) {
    // Either recovery isn't set up server-side (shouldn't happen — we
    // have a cached mnemonic) or we're talking to an older server that
    // doesn't emit wrap_rev yet. Treat as "couldn't verify" rather than
    // invalidating every cache when the server rolls back.
    return { mnemonic: cached.mnemonic, unverified: true };
  }

  if (cached.wrap_rev === null) {
    // Legacy cache written before wrap_rev shipped. Accept it if the
    // server has never rotated; otherwise it's definitionally stale.
    if (serverRev === 1) {
      await cacheMnemonicLocally(accountId, cached.mnemonic, 1);
      return { mnemonic: cached.mnemonic };
    }
    await clearCachedMnemonic(accountId);
    return {
      mnemonic: null,
      stale: true,
      message: 'Your recovery code was changed on another device. Enter the current one to see it here.',
    };
  }

  if (serverRev > cached.wrap_rev) {
    await clearCachedMnemonic(accountId);
    return {
      mnemonic: null,
      stale: true,
      message: 'Your recovery code was changed on another device. Enter the current one to see it here.',
    };
  }

  // serverRev === cached.wrap_rev (or, defensively, cached is somehow
  // ahead — we treat that as fresh too; the next rotate reconciles it).
  return { mnemonic: cached.mnemonic };
};

/** Local-only reset. Server wrap is untouched; user can recover again. */
export const resetSnapshotRecoveryLocal = () => async (): Promise<void> => {
  const { accountId } = getSessionIds();
  if (!accountId) return;
  await Promise.all([wipeAccountRootKey(accountId), clearCachedMnemonic(accountId)]);
};

// ─── Create snapshot ──────────────────────────────────────────────────────────
// The server fetches all project data from the DB, serializes, and encrypts.
// The client receives the encrypted blob, saves it to disk, and syncs to iCloud.
// No Redux state is read for backup — the DB is always the source of truth.

export const createSnapshot = (
  projectId: number | string,
  opts?: { note?: string },
) => async (dispatch: any): Promise<{
  success: boolean;
  message?: string;
  needsRecovery?: boolean;
  /**
   * Populated ONLY when this call was the first-ever backup for the account
   * and silently triggered `setupSnapshotRecovery`. The caller is expected
   * to surface the mnemonic immediately via a save-your-code sheet so the
   * user walks away with a written copy — this is the ONE moment in the
   * lifecycle where the mnemonic is eagerly returned. `revealRecoveryMnemonic`
   * is the lazy counterpart for later "show me my code" flows.
   */
  mnemonic?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  dispatch({ type: SET_SNAPSHOT_SYNCING, payload: true });
  dispatch({ type: SET_SNAPSHOT_ERROR, payload: null });

  // Captured across the status/setup branch so we can return it to the caller
  // after the snapshot itself succeeds. We intentionally don't surface the
  // mnemonic if the encrypt/upload step fails — the user should see ONE
  // success screen that bundles "backup done + here's your code" rather than
  // a half-successful flow that hands out a code for an account that has no
  // backup yet.
  let firstTimeMnemonic: string | undefined;

  try {
    // 0. v2 requires the canonical account root key on this device. If it's
    //    missing we don't lazily generate one — that would make a per-device
    //    key the user can't use anywhere else. We surface needsRecovery so
    //    the caller can route to setup (first device) or restore (any other
    //    device). The setup call below is the only place that ever creates
    //    the root key.
    const status = await dispatch(getSnapshotRecoveryStatus());
    if (!status.hasLocalKey) {
      if (!status.serverHasWrap) {
        const setupRes = await dispatch(setupSnapshotRecovery());
        if (!setupRes.success) {
          return {
            success: false,
            message: setupRes.message ?? 'Recovery setup failed.',
            needsRecovery: true,
          };
        }
        // Hold on to the mnemonic so we can hand it back to the caller AFTER
        // the encrypt step succeeds (see end of try block).
        firstTimeMnemonic = setupRes.mnemonic;
      } else {
        // Wrap exists on server but root key is missing locally — caller
        // must collect the mnemonic and call restoreSnapshotRecovery first.
        return {
          success: false,
          message: 'Enter your recovery code on this device before backing up.',
          needsRecovery: true,
        };
      }
    }

    // 1. Fetch (or lazily create) the per-project salt. This is the same salt
    //    `/snapshots/salt` returns on restore; creating or fetching it here
    //    lets us derive the v2 SMK on the client BEFORE we ask the server to
    //    encrypt. We send that SMK to the server as `encryption_key_hex`;
    //    the server encrypts with it once and drops it, never persisting the
    //    key. Without the canonical account root key, an attacker with only
    //    the server's DB + S3 state cannot decrypt the resulting envelope.
    const saltRes = await apiGet(`/snapshots/salt/${projectId}/${accountId}/${cognitoId}`);
    const saltHex: string | undefined = saltRes?.payload?.salt;
    if (!saltHex) {
      return { success: false, message: 'Could not obtain snapshot salt from server.' };
    }
    const smkV2 = await deriveSmkForNewSnapshot(accountId, projectId, saltHex);
    if (!smkV2) {
      // Defensive: status said we had the local key, but it disappeared
      // between then and now (rare race, e.g. another app cleared SecureStore).
      return {
        success: false,
        message: 'Account root key is missing. Restore from your recovery code.',
        needsRecovery: true,
      };
    }

    // 2. Ask the server to build and encrypt the snapshot. The server queries
    //    the DB directly — that is the authoritative source. The response
    //    contains only metadata + a presigned S3 GET URL (`download_url`); the
    //    encrypted envelope itself is uploaded to S3 by the server because
    //    hex-encoded ciphertext routinely exceeds API Gateway's 6 MB response
    //    cap for real projects.
    const res = await apiPost(`/snapshots/create/${projectId}/${accountId}/${cognitoId}`, {
      note: opts?.note ?? null,
      encryption_key_hex: bytesToHex(smkV2),
      envelope_version: SNAPSHOT_ENVELOPE_VERSION_V2,
    });

    if (!res?.success || !res?.payload) {
      return { success: false, message: res?.message ?? 'Server failed to create snapshot.' };
    }

    const meta = res.payload as {
      download_url?: string;
      encrypted_data?: string;
      iv?: string;
      auth_tag?: string;
      schema_version: number;
      snapshot_version: number;
      created_at: string;
      checksum: string;
      size_bytes: number;
    };
    const { snapshot_version: nextVersion, download_url: downloadUrl } = meta;

    // 2. Fetch the full encrypted envelope from the presigned URL. We keep a
    //    narrow inline-data fallback so an older server (or a future small-
    //    payload optimization) that still embeds the envelope in the JSON
    //    response continues to work without a client update.
    let blob: any;
    if (downloadUrl) {
      const envelopeRes = await fetch(downloadUrl);
      if (!envelopeRes.ok) {
        return {
          success: false,
          message: `Failed to download snapshot blob (HTTP ${envelopeRes.status}).`,
        };
      }
      blob = await envelopeRes.json();
    } else if (meta.encrypted_data && meta.iv && meta.auth_tag) {
      blob = meta;
    } else {
      return { success: false, message: 'Server returned no snapshot blob.' };
    }

    // 3. Persist the encrypted blob on-device.
    await saveSnapshot(projectId, nextVersion, blob);

    // 4. Cache the v2 SMK we just derived in the biometric-gated SecureStore
    //    slot so restore / share avoid a SecureStore round-trip for the
    //    device root key. HKDF itself is fast (sub-ms) but the cache keeps
    //    the biometric-prompt UX consistent with the v1 path.
    await cacheSnapshotKey(projectId, smkV2, { version: SNAPSHOT_ENVELOPE_VERSION_V2 });

    // 5. Sync to iCloud in the background. Failure does not fail the backup.
    //    Pass the plan's display name so the iCloud file surfaces as
    //    Vault/{plan name}/Snapshot v{N} — {Mon D, YYYY}.eleplan in Files.app.
    const { store: storeForSync } = require('../index');
    const planNameForSync: string | undefined =
      storeForSync.getState()?.projects?.list?.[String(projectId)]?.name;
    syncSnapshotToICloud(projectId, nextVersion, {
      planLabel: planNameForSync,
      createdAt: blob.created_at,
    }).catch(() => {});

    dispatch({
      type: SET_SNAPSHOT_LAST_BACKUP,
      payload: { project_id: projectId, timestamp: blob.created_at },
    });

    // 6. Refresh the version list so the UI reflects the new entry. This MUST
    //    be awaited — callers (handleBackupNow) immediately kick off
    //    refreshBackupStatus, which reads state.snapshots.versions synchronously
    //    and falls back to {missing, missing} if the list is empty. Without the
    //    await, the backup completes on disk but the status cards stay
    //    "Not backed up" until the next manual refresh.
    await dispatch(fetchSnapshotVersions(projectId));

    // If this call implicitly set up recovery for the first time, hand the
    // mnemonic back so the caller can force-display it. This is the single
    // "eager reveal" moment — future reveals use the lazy /reveal path.
    return firstTimeMnemonic
      ? { success: true, mnemonic: firstTimeMnemonic }
      : { success: true };
  } catch (err: any) {
    const msg = err?.message ?? 'Snapshot backup failed.';
    dispatch({ type: SET_SNAPSHOT_ERROR, payload: msg });
    return { success: false, message: msg };
  } finally {
    dispatch({ type: SET_SNAPSHOT_SYNCING, payload: false });
  }
};

// ─── Open snapshot in View Mode ───────────────────────────────────────────────
// This is a READ-ONLY hydrate of Redux from a decrypted snapshot blob. The live
// PostgreSQL database is not touched. The user is placed in "View Mode" until
// they explicitly exit — at which point live data is re-fetched and the view is
// cleared. Think of it as opening a stored envelope, not replacing data.

export const openSnapshotView = (
  projectId: number | string,
  version: number,
) => async (dispatch: any): Promise<{ success: boolean; message?: string; needsRecovery?: boolean }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  dispatch({ type: SET_SNAPSHOT_SYNCING, payload: true });
  dispatch({ type: SET_SNAPSHOT_ERROR, payload: null });

  try {
    const envelope = await loadSnapshot(projectId, version);
    if (!envelope) return { success: false, message: 'Snapshot file not found on this device.' };

    // Integrity check: hash the on-disk ciphertext and compare it to the
    // checksum the server recorded at backup time. If anything about the
    // ciphertext, IV, or auth tag has changed the snapshot is refused before
    // decryption. GCM would catch tampering inside `decrypt` regardless, but
    // verifying the checksum first lets us show the user a clearer error and
    // refuse to touch a corrupted file at all.
    try {
      const versionsRes = await apiGet(`/snapshots/versions/${projectId}/${accountId}/${cognitoId}`);
      const serverVersions: Array<{ version: number; checksum?: string }> =
        Array.isArray(versionsRes?.payload) ? versionsRes.payload : [];
      const match = serverVersions.find((v) => Number(v.version) === Number(version));
      if (match?.checksum) {
        const localChecksum = await envelopeChecksum(envelope);
        if (localChecksum !== match.checksum) {
          return {
            success: false,
            message: 'Snapshot integrity check failed — the file on this device no longer matches the stored copy. Restore was stopped before decryption.',
          };
        }
      }
      // If no server-side checksum is available (older snapshot, offline, etc.)
      // we intentionally fall through — GCM will still reject tampered bytes.
    } catch {
      // Network/auth errors are non-fatal: GCM auth-tag verification inside
      // decryptSnapshot remains as the last line of defense.
    }

    // Dispatch on the envelope version so we derive the matching SMK. A v1
    // envelope (no `version` field) opens with the PBKDF2 key the server
    // still knows how to reproduce. A v2 envelope requires the device root
    // key — if it's absent (fresh install on a new device), we surface that
    // explicitly rather than silently returning a meaningless auth failure.
    const envelopeVersion = getEnvelopeVersion(envelope);
    let smk = await loadSnapshotKey(projectId, {
      version: envelopeVersion,
      prompt: 'Authenticate to open health snapshot',
    });

    if (!smk) {
      const saltRes = await apiGet(`/snapshots/salt/${projectId}/${accountId}/${cognitoId}`);
      const saltHex = saltRes?.payload?.salt;
      if (!saltHex) return { success: false, message: 'Could not retrieve snapshot key — try backing up again.' };
      smk = await deriveSmkForEnvelope(envelope, accountId, projectId, saltHex);
      if (!smk) {
        // v2 envelope and no account root key on this device → user must
        // complete the mnemonic recovery flow before any v2 snapshot opens.
        // `needsRecovery` is a signal to the caller UI to present the
        // restore-from-mnemonic sheet; without it the screen has no way to
        // route the user out of the dead-end.
        return {
          success: false,
          needsRecovery: true,
          message: 'This snapshot is locked to your account root key, which isn’t on this device. Enter your 12-word recovery code to unlock it.',
        };
      }
      await cacheSnapshotKey(projectId, smk, { version: envelopeVersion });
    }

    const snapshotJson = decryptSnapshot(smk, envelope);
    if (!snapshotJson) return { success: false, message: 'Failed to decrypt snapshot — key may be invalid.' };

    // Rehydrate Redux for this project from the decrypted payload. Live DB is
    // untouched; exiting view mode re-fetches everything.
    deserializeSnapshot(dispatch, snapshotJson as any);

    dispatch({
      type: SET_SNAPSHOT_VIEW_MODE,
      payload: {
        project_id: String(projectId),
        version,
        entered_at: new Date().toISOString(),
        created_at: (snapshotJson as any)?.created_at ?? null,
      },
    });

    return { success: true };
  } catch (err: any) {
    const msg = err?.message ?? 'Snapshot could not be opened.';
    dispatch({ type: SET_SNAPSHOT_ERROR, payload: msg });
    return { success: false, message: msg };
  } finally {
    dispatch({ type: SET_SNAPSHOT_SYNCING, payload: false });
  }
};

// Back-compat alias for any callers that still refer to the old name.
export const restoreSnapshot = openSnapshotView;

// ─── Exit snapshot View Mode ──────────────────────────────────────────────────
// Clears the view-mode flag and re-fetches every slice of data for the project
// from the live DB so the app returns to the user's real account state.

export const exitSnapshotView = (
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean }> => {
  const pid = String(projectId);
  dispatch({ type: CLEAR_SNAPSHOT_VIEW_MODE });

  // Lazy requires avoid a cycle with the actions index.
  try {
    const { fetchMedications } = require('./medications');
    const { fetchContacts } = require('./contacts');
    const { fetchNotes } = require('./notes');
    const { fetchDocuments } = require('./documents');
    const { fetchCalendarEvents } = require('./calendar');
    const { getReminders } = require('./reminders');
    const { getResponses } = require('./responses');
    const { fetchSegments } = require('./blocks');
    const { fetchVaultEntries } = require('./vault');
    const { getMessages } = require('./messages');

    await Promise.all([
      dispatch(fetchMedications(pid)),
      dispatch(fetchContacts(pid)),
      dispatch(fetchNotes(pid)),
      dispatch(fetchDocuments(pid)),
      dispatch(fetchCalendarEvents(pid)),
      dispatch(getReminders(pid, true)),
      dispatch(getResponses(pid, true)),
      dispatch(fetchSegments(pid)),
      dispatch(fetchVaultEntries(pid)),
      dispatch(getMessages(pid, true)),
    ].map((p: Promise<unknown>) => p.catch(() => undefined)));
  } catch {
    // Even if a re-fetch fails, the view-mode flag is cleared — the user has
    // exited the snapshot view. Next pull-to-refresh will resync.
  }

  return { success: true };
};

// ─── Create share link ────────────────────────────────────────────────────────

export const createSnapshotShare = (
  projectId: number | string,
  version: number,
  opts: { expiresInSeconds?: number; maxUses?: number },
) => async (dispatch: any): Promise<{
  success: boolean;
  shareUrl?: string;
  passUrl?: string | null;
  expiresAt?: number;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  try {
    // 1. Load the encrypted blob from device.
    const envelope = await loadSnapshot(projectId, version);
    if (!envelope) return { success: false, message: 'Snapshot file not found on this device.' };

    // 2. Load SMK for the envelope's version — triggers biometric prompt.
    const envelopeVersion = getEnvelopeVersion(envelope);
    let smk = await loadSnapshotKey(projectId, {
      version: envelopeVersion,
      prompt: 'Authenticate to create share link',
    });

    if (!smk) {
      const saltRes = await apiGet(`/snapshots/salt/${projectId}/${accountId}/${cognitoId}`);
      const saltHex = saltRes?.payload?.salt;
      if (!saltHex) return { success: false, message: 'Could not retrieve snapshot key.' };
      smk = await deriveSmkForEnvelope(envelope, accountId, projectId, saltHex);
      if (!smk) {
        return {
          success: false,
          message: 'Cannot share this snapshot — your account root key isn’t on this device. Enter your 12-word recovery code and try again.',
        };
      }
      await cacheSnapshotKey(projectId, smk, { version: envelopeVersion });
    }

    // 3. Generate a one-time share key — never sent to the server.
    const shareKey = await generateSnapshotShareKey();
    const shareKeyHex = bytesToHex(shareKey);

    // 4. Re-encrypt snapshot with the share key.
    const shareBlob = await encryptSnapshotForShare(smk, envelope, shareKey);

    // 5. Ask backend for a presigned S3 upload URL + short-link code.
    const shareRes = await apiPost(`/snapshots/share/${projectId}/${accountId}/${cognitoId}`, {
      expires_in_seconds: opts.expiresInSeconds ?? 86400,
      max_uses: opts.maxUses ?? 1,
    });

    if (!shareRes?.success || !shareRes?.payload?.upload_url) {
      return { success: false, message: shareRes?.message ?? 'Failed to create share.' };
    }

    const { upload_url, code, expires_at, short_url, pass_url } = shareRes.payload;

    // 6. Upload share-encrypted blob directly to S3 from the client.
    const uploadRes = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: JSON.stringify(shareBlob),
    });

    if (!uploadRes.ok) {
      return { success: false, message: 'Failed to upload share blob.' };
    }

    // 7. Build the shareable URL. Prefer the server-supplied short_url (built
    // from the stage's LINK_HOST — same SSM source used by links/create) so
    // dev/prod hosts and any future domain swap are driven from backend env,
    // not hardcoded in mobile. Fall back to the web app base URL if the server
    // didn't return one (older deploy).
    //
    // The share key is appended as the URL fragment (#...) — browsers and HTTP
    // clients never send fragments to servers, so the key stays on the device
    // of whoever opens the link. It is a one-time, per-share key; it cannot
    // decrypt the on-device snapshot, future snapshots, or live project data.
    const baseShareUrl = short_url || `${config.webApp.baseUrl}/share/${code}`;
    const shareUrl = `${baseShareUrl}#${shareKeyHex}`;

    // Refresh the shares list so the newly-created link appears immediately in
    // any Active Shares UI without waiting for a pull-to-refresh.
    dispatch(listSnapshotShares(projectId));

    // `passUrl` is the wallet-pass install link for the paired snapshot_pass
    // short-link created server-side. It's already fully-qualified (stage-
    // aware API host) and does NOT take a URL fragment — the recipient opens
    // it in iOS Safari and the .pkpass bytes are handed to Wallet directly.
    // It is surfaced to the Done step of the share sheet so the owner can
    // copy/share it alongside the snapshot URL without waiting for a list
    // refresh.
    return { success: true, shareUrl, passUrl: pass_url ?? null, expiresAt: expires_at };
  } catch (err: any) {
    return { success: false, message: err?.message ?? 'Share creation failed.' };
  }
};

// ─── List share links ─────────────────────────────────────────────────────────

export const listSnapshotShares = (
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  try {
    const res = await apiGet(`/snapshots/shares/${projectId}/${accountId}/${cognitoId}`);
    const shares = Array.isArray(res?.payload) ? res.payload : [];
    dispatch({ type: SET_SNAPSHOT_SHARES, payload: { project_id: projectId, shares } });
    return { success: true };
  } catch (err: any) {
    // On error, clear to empty rather than leave stale — users should see an
    // up-to-date list or nothing.
    dispatch({ type: SET_SNAPSHOT_SHARES, payload: { project_id: projectId, shares: [] } });
    return { success: false, message: err?.message ?? 'Failed to load shares.' };
  }
};

// ─── Revoke a share link ──────────────────────────────────────────────────────
// Calls DELETE /snapshots/shares/{project}/{account}/{cognito}/{code}. The
// server expires the short-link and deletes the ciphertext from S3 before
// returning. On success we optimistically flip the local status to 'expired'
// so the UI updates immediately; a background refresh reconciles.

export const revokeSnapshotShare = (
  projectId: number | string,
  code: string,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  try {
    const res = await apiDelete(`/snapshots/shares/${projectId}/${accountId}/${cognitoId}/${code}`);
    if (!res?.success) {
      return { success: false, message: res?.message ?? 'Failed to revoke share.' };
    }
    dispatch({ type: REMOVE_SNAPSHOT_SHARE, payload: { project_id: projectId, code } });
    // Background refresh to pick up server-side state changes.
    dispatch(listSnapshotShares(projectId));
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message ?? 'Failed to revoke share.' };
  }
};

// ─── Owner Health Passport (Apple Wallet) ─────────────────────────────────────
// Generates (or regenerates) the owner's never-expiring Health Passport for
// the given project and returns a fully-qualified `.pkpass` install URL. The
// snapshot screen uses this for a dedicated "Add to Apple Wallet" button —
// the share-path version of this same pass (for recipients) is minted
// automatically at create-share time and surfaced as `share.pass_url` on the
// per-share row.
//
// NOTE: intentionally NOT cached in Redux. The endpoint is cheap (S3-backed
// pass already exists, just re-issues a signed URL on most calls) and always-
// fresh URLs avoid the "I tapped it, nothing happened" class of bugs caused
// by stale signed URLs. Errors bubble up to the caller so the screen can
// show a notification.

export const generateOwnerWalletPassUrl = (
  projectId: number | string,
) => async (): Promise<{ success: boolean; passUrl?: string; emergencyCardUrl?: string | null; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated.' };

  try {
    const res = await apiProjectsPost(`/generate-wallet-pass/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
    if (!res?.success) {
      return {
        success: false,
        message: res?.message ?? 'Failed to generate wallet pass.',
        emergencyCardUrl: res?.emergency_card_url ?? null,
      };
    }
    return { success: true, passUrl: res?.payload?.pass_url };
  } catch (err: any) {
    return { success: false, message: err?.message ?? 'Failed to generate wallet pass.' };
  }
};

// ─── Refresh backup status ────────────────────────────────────────────────────

export const refreshBackupStatus = (
  projectId: number | string,
) => async (dispatch: any): Promise<void> => {
  dispatch({
    type: SET_SNAPSHOT_BACKUP_STATUS,
    payload: {
      project_id: projectId,
      statuses: [
        { location: 'device', status: 'checking', checkedAt: new Date().toISOString() },
        { location: 'icloud', status: 'checking', checkedAt: new Date().toISOString() },
      ],
    },
  });

  try {
    const { store } = require('../index');
    const versions: any[] = store.getState().snapshots?.versions?.[String(projectId)] ?? [];
    if (!versions.length) {
      dispatch({
        type: SET_SNAPSHOT_BACKUP_STATUS,
        payload: {
          project_id: projectId,
          statuses: [
            { location: 'device', status: 'missing', checkedAt: new Date().toISOString() },
            { location: 'icloud', status: 'missing', checkedAt: new Date().toISOString() },
          ],
        },
      });
      return;
    }

    // The per-location scanner needs the full registry, not just the latest —
    // that's how it can report "you have v2 here, latest is v3" (the 'outdated'
    // case) instead of flatly reporting 'missing' because the latest isn't on
    // this particular device. Plan label feeds the Vault filename; per-entry
    // created_at drives the filename of each older version we might scan for.
    const planNameForCheck: string | undefined =
      store.getState()?.projects?.list?.[String(projectId)]?.name;
    const registry = versions.map((v) => ({
      version: v.version,
      checksum: v.checksum,
      created_at: v.created_at,
    }));
    const latest = registry[0];

    // Best-effort: if iCloud has the latest but this device doesn't, copy it
    // down before we read status. Net effect: a new device auto-catches up
    // after sign-in and the chips read "Verified" without user action. If
    // iCloud isn't configured / different Apple ID / no latest on iCloud, this
    // no-ops and we fall through to the honest 'outdated' state instead.
    try {
      await hydrateLatestFromICloud(projectId, registry, {
        planLabel: planNameForCheck,
        createdAt: latest.created_at,
      });
    } catch {
      // Hydration is best-effort; never let it block the status read.
    }

    const statuses = await checkBackupStatus(projectId, registry, {
      planLabel: planNameForCheck,
      createdAt: latest.created_at,
    });
    dispatch({ type: SET_SNAPSHOT_BACKUP_STATUS, payload: { project_id: projectId, statuses } });
  } catch {
    dispatch({
      type: SET_SNAPSHOT_BACKUP_STATUS,
      payload: {
        project_id: projectId,
        statuses: [
          { location: 'device', status: 'missing', checkedAt: new Date().toISOString() },
          { location: 'icloud', status: 'unavailable', checkedAt: new Date().toISOString() },
        ],
      },
    });
  }
};
